// PERSAN/teklif.html'in mantığı — şablonun Excel dosyasını parse edip
// dolgulu hücreleri input, formülleri canlı çıktı olarak render eder.
// Kütüphaneler: xlsx (formül parse), exceljs (dolgu+dropdown), hyperformula (canlı hesap).
import React, { useEffect, useRef, useState, useCallback } from 'react';
import api from '../../services/api';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { HyperFormula } from 'hyperformula';

const fmtTR = (n) => isFinite(n) ? new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) : '—';

const OFFICE_THEME_COLORS = ['FFFFFF', '000000', 'E7E6E6', '44546A', '5B9BD5', 'ED7D31', 'A5A5A5', 'FFC000', '4472C4', '70AD47'];

const applyTint = (hex, tint) => {
  if (!hex || hex.length < 6) return hex;
  let r = parseInt(hex.slice(0, 2), 16);
  let g = parseInt(hex.slice(2, 4), 16);
  let b = parseInt(hex.slice(4, 6), 16);
  if (tint > 0) { r = Math.round(r + (255 - r) * tint); g = Math.round(g + (255 - g) * tint); b = Math.round(b + (255 - b) * tint); }
  else if (tint < 0) { r = Math.round(r * (1 + tint)); g = Math.round(g * (1 + tint)); b = Math.round(b * (1 + tint)); }
  const cl = (v) => Math.max(0, Math.min(255, v));
  return [cl(r), cl(g), cl(b)].map(v => v.toString(16).padStart(2, '0')).join('').toUpperCase();
};
const themeToRgb = (theme, tint) => applyTint(OFFICE_THEME_COLORS[theme] || 'FFFFFF', tint || 0);

const hasFill = (cell) => !!(cell?.s?.fill && (cell.s.fill.fgColor || cell.s.fill.bgColor));

// Bir hücrenin INPUT olabilmesi için: formül değil + değeri var + dolgu var + (sayı VEYA referans verilen metin)
const isInputCell = (cell, addr, referencedCells) => {
  if (!cell || cell.f) return false;
  if (cell.v == null || cell.v === '') return false;
  if (!hasFill(cell)) return false;
  if (typeof cell.v === 'number') return true;
  if (typeof cell.v === 'string' && referencedCells.has(addr)) return true;
  return false;
};

const buildReferencedCells = (sheet, range) => {
  const refs = new Set();
  for (let r = 0; r <= range.e.r; r++) {
    for (let c = 0; c <= range.e.c; c++) {
      const cell = sheet[XLSX.utils.encode_cell({ r, c })];
      if (!cell || !cell.f) continue;
      const matches = String(cell.f).match(/\$?[A-Z]+\$?[0-9]+/g) || [];
      matches.forEach(m => refs.add(m.replace(/\$/g, '')));
    }
  }
  return refs;
};

const resolveValidationOptions = (formula, sheet) => {
  if (!formula) return [];
  let f = String(formula).trim();
  if (f.startsWith('=')) f = f.slice(1);
  if ((f.startsWith('"') && f.endsWith('"')) || (f.startsWith("'") && f.endsWith("'"))) {
    return f.slice(1, -1).split(/[,;]/).map(s => s.trim()).filter(Boolean);
  }
  if (f.includes(',') && !f.includes('!') && !f.includes(':') && !/[A-Z]\d/.test(f)) {
    return f.split(',').map(s => s.trim()).filter(Boolean);
  }
  const rangeRe = /(?:'?[^!]*'?!)?\$?([A-Z]+)\$?(\d+):\$?([A-Z]+)\$?(\d+)/;
  const rm = f.match(rangeRe);
  if (rm) {
    const c1 = XLSX.utils.decode_col(rm[1]); const r1 = parseInt(rm[2], 10) - 1;
    const c2 = XLSX.utils.decode_col(rm[3]); const r2 = parseInt(rm[4], 10) - 1;
    const opts = [];
    for (let r = Math.min(r1, r2); r <= Math.max(r1, r2); r++) {
      for (let c = Math.min(c1, c2); c <= Math.max(c1, c2); c++) {
        const cell = sheet[XLSX.utils.encode_cell({ r, c })];
        if (cell && cell.v != null && cell.v !== '' && !cell.f) opts.push(String(cell.v));
      }
    }
    return opts;
  }
  const cellRe = /(?:'?[^!]*'?!)?\$?([A-Z]+)\$?(\d+)$/;
  const cm = f.match(cellRe);
  if (cm) {
    const c = XLSX.utils.decode_col(cm[1]); const r = parseInt(cm[2], 10) - 1;
    const cell = sheet[XLSX.utils.encode_cell({ r, c })];
    if (cell && cell.v != null) return [String(cell.v)];
  }
  return [];
};

const fmtCellValue = (v, cell) => {
  if (v === null || v === undefined || v === '') return '';
  if (typeof v === 'object') {
    if ('value' in v) return v.value;
    if ('type' in v) return '#' + v.type;
    return String(v);
  }
  if (typeof v === 'boolean') return v ? 'DOĞRU' : 'YANLIŞ';
  if (typeof v === 'string') return v;
  if (typeof v === 'number') {
    const fmt = cell?.z;
    if (fmt && fmt !== 'General' && typeof XLSX.SSF !== 'undefined') {
      try { return XLSX.SSF.format(fmt, v); } catch (e) {}
    }
    if (Number.isInteger(v) && Math.abs(v) < 100000) return String(v);
    return v.toLocaleString('tr-TR', { maximumFractionDigits: 4 });
  }
  return String(v);
};

const findLabel = (sheet, r, c) => {
  for (let dc = 1; dc <= 3; dc++) {
    const c2 = c - dc; if (c2 < 0) break;
    const cell = sheet[XLSX.utils.encode_cell({ r, c: c2 })];
    if (!cell || cell.f || cell.v == null) continue;
    const v = String(cell.v).trim();
    if (!v || !isNaN(Number(v))) continue;
    return v;
  }
  for (let dr = 1; dr <= 2; dr++) {
    const r2 = r - dr; if (r2 < 0) break;
    const cell = sheet[XLSX.utils.encode_cell({ r: r2, c })];
    if (!cell || cell.f || cell.v == null) continue;
    const v = String(cell.v).trim();
    if (!v || !isNaN(Number(v))) continue;
    return v;
  }
  return null;
};

const classifyOutput = (label) => {
  const lt = String(label || '').toLowerCase()
    .replace(/i̇/g, 'i').replace(/ı/g, 'i').replace(/ş/g, 's').replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ö/g, 'o').replace(/ü/g, 'u');
  // Döviz satışları önce — "euro satis fiyati" varyantı 'satis fiyat' substring'ine takılmasın
  if (lt.includes('euro satis')) return 'secondary';
  if (lt.includes('dolar satis')) return 'secondary';
  if (lt.includes('kar %') || lt.includes('kar yuz') || lt.includes('kar oran')) return 'percent';
  if (lt.includes('satis fiyat')) return 'primary';
  // kar tutarı / sistem maliyeti / işçilik / toplam maliyet / pergola toplam vb. — kullanıcı tarafına gösterilmez
  return 'hidden';
};

// onChange snapshot'ında (kayıt için) saklanan özet değerler.
// Sıra önemli: 'euro satis' ve 'dolar satis' önce gelmeli ki 'satis fiyat' substring eşleşmesi onları yutmasın.
const SUMMARY_KEYS = [
  { k: 'euro satis', label: 'EURO SATIŞ' },
  { k: 'dolar satis', label: 'DOLAR SATIŞ' },
  { k: 'satis fiyat', label: 'SATIŞ FİYATI', primary: true },
  { k: 'kar %', label: 'KAR %', percent: true },
];
const normalize = (s) => String(s || '').toLowerCase().trim()
  .replace(/i̇/g, 'i').replace(/ı/g, 'i').replace(/ş/g, 's').replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ö/g, 'o').replace(/ü/g, 'u');

const buildSummary = (sheet, range, hf, sheetIdHF) => {
  const found = []; const seen = new Set();
  for (let r = 0; r <= range.e.r; r++) {
    for (let c = 0; c <= range.e.c; c++) {
      const cell = sheet[XLSX.utils.encode_cell({ r, c })];
      if (!cell || typeof cell.v !== 'string') continue;
      const txt = normalize(cell.v);
      const match = SUMMARY_KEYS.find(k => txt.includes(normalize(k.k)));
      if (!match || seen.has(match.label)) continue;
      const candidates = [{ r, c: c + 1 }, { r, c: c + 2 }, { r, c: c + 3 }, { r: r + 1, c }, { r: r + 1, c: c + 1 }];
      for (const p of candidates) {
        if (p.c > range.e.c || p.r > range.e.r) continue;
        const v = hf.getCellValue({ sheet: sheetIdHF, row: p.r, col: p.c });
        if (typeof v === 'number') { found.push({ ...match, value: v }); seen.add(match.label); break; }
      }
    }
  }
  found.sort((a, b) => (b.primary ? 1 : 0) - (a.primary ? 1 : 0));
  return found;
};

// ─────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────
export default function ExcelCalcForm({ templateId, initialValues, onChange }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState(null);
  const [summary, setSummary] = useState([]);
  const [tick, setTick] = useState(0); // recalc flash trigger

  const stateRef = useRef({ sheet: null, range: null, hf: null, sheetIdHF: null, originalData: null, applied: false });
  // formData'nın ref kopyası — setFormData functional updater'ı bazen async queue'lanabildiği
  // için recalc, snapshot'ı kurmadan önce buradan okur (stale state veya null değil).
  const formDataRef = useRef(null);

  const recalc = useCallback(() => {
    const { sheet, range, hf, sheetIdHF } = stateRef.current;
    if (!sheet || !hf) return;
    const prev = formDataRef.current;
    if (!prev) return;
    const newInputs  = prev.inputs.map(i => ({ ...i, value: hf.getCellValue({ sheet: sheetIdHF, row: i.r, col: i.c }) }));
    const newOutputs = prev.outputs.map(o => ({ ...o, value: hf.getCellValue({ sheet: sheetIdHF, row: o.r, col: o.c }) }));
    formDataRef.current = { inputs: newInputs, outputs: newOutputs };
    setFormData({ inputs: newInputs, outputs: newOutputs });
    const newSummary = buildSummary(sheet, range, hf, sheetIdHF);
    setSummary(newSummary);
    setTick(t => t + 1);
    if (onChange) {
      // Tam snapshot — input değerleri + çıktı değerleri + özet + label'lar.
      // Tarihsel doğruluk için: Excel'in sabitleri değişse bile bu snapshot'tan
      // geçmiş teklifin orijinal fiyatlarını okuyabiliriz.
      const values = {};
      newInputs.forEach(i => { values[i.addr] = i.value; });
      // Label'lı input listesi — teklife aktarımda iç notlara yazılmak üzere
      const inputs_labeled = newInputs.map(i => ({ addr: i.addr, label: i.label, value: i.value }));
      const outputs = {};
      newOutputs.forEach(o => { outputs[o.addr] = { label: o.label, value: o.value }; });
      const summarySnapshot = newSummary.map(s => ({
        key: s.k, label: s.label, value: s.value,
        primary: !!s.primary, percent: !!s.percent,
      }));
      const salesPrice = (newSummary.find(s => s.primary)?.value) || null;
      onChange({ values, inputs_labeled, outputs, summary: summarySnapshot, salesPrice });
    }
  }, [onChange]);

  useEffect(() => {
    if (!templateId) { setFormData(null); setSummary([]); return; }
    let cancelled = false;
    setLoading(true); setError('');
    (async () => {
      try {
        const r = await api.get('/pergola/templates/' + templateId + '/download', { responseType: 'arraybuffer' });
        if (cancelled) return;
        const buf = r.data;

        // 1) SheetJS — formül + değer
        const wb = XLSX.read(buf, { cellFormula: true, cellNF: true });
        const sheetName = wb.SheetNames[0];
        const sheet = wb.Sheets[sheetName];
        const range = XLSX.utils.decode_range(sheet['!ref']);
        for (const addr in sheet) { if (!addr.startsWith('!') && !sheet[addr].s) sheet[addr].s = {}; }

        // 2) ExcelJS — dolgu + font + data validation
        const ejsWb = new ExcelJS.Workbook();
        await ejsWb.xlsx.load(buf);
        const ws = ejsWb.worksheets[0];
        ws.eachRow({ includeEmpty: false }, (row, rowNumber) => {
          const r = rowNumber - 1;
          row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
            const c = colNumber - 1;
            const addr = XLSX.utils.encode_cell({ r, c });
            let target = sheet[addr] || (sheet[addr] = { s: {} });
            target.s = target.s || {};
            if (cell.fill && cell.fill.type === 'pattern') {
              const fc = cell.fill.fgColor; const bc = cell.fill.bgColor;
              if (fc || bc) {
                target.s.fill = {};
                if (fc) {
                  const fillCol = {};
                  if (fc.argb) fillCol.rgb = fc.argb;
                  else if (fc.theme !== undefined) fillCol.rgb = themeToRgb(fc.theme, fc.tint);
                  if (fillCol.rgb) target.s.fill.fgColor = fillCol;
                }
                if (bc && !target.s.fill.fgColor) {
                  const fillCol = {};
                  if (bc.argb) fillCol.rgb = bc.argb;
                  else if (bc.theme !== undefined) fillCol.rgb = themeToRgb(bc.theme, bc.tint);
                  if (fillCol.rgb) target.s.fill.bgColor = fillCol;
                }
              }
            }
            if (cell.dataValidation && cell.dataValidation.type === 'list') {
              const formulae = cell.dataValidation.formulae;
              if (formulae && formulae.length > 0) {
                target.dv = { type: 'list', formula: String(formulae[0]) };
              }
            }
          });
        });

        // 3) Referans verilen hücreleri çıkar (text input tespiti için)
        const referencedCells = buildReferencedCells(sheet, range);

        // 4) Dropdown opsiyonlarını çöz
        for (const addr in sheet) {
          if (addr.startsWith('!')) continue;
          const c = sheet[addr];
          if (c.dv && c.dv.formula) c.dv.options = resolveValidationOptions(c.dv.formula, sheet);
        }

        // 5) HyperFormula
        const hf = HyperFormula.buildEmpty({ licenseKey: 'gpl-v3' });
        hf.addSheet(sheetName);
        const sheetIdHF = hf.getSheetId(sheetName);
        const data = [];
        for (let r = 0; r <= range.e.r; r++) {
          const row = [];
          for (let c = 0; c <= range.e.c; c++) {
            const addr = XLSX.utils.encode_cell({ r, c });
            const cell = sheet[addr];
            if (!cell) row.push(null);
            else if (cell.f) row.push('=' + cell.f);
            else row.push(cell.v);
          }
          data.push(row);
        }
        const originalData = JSON.parse(JSON.stringify(data));
        hf.setSheetContent(sheetIdHF, data);

        // 6) Form bölgesini çıkar — sadece input hücrelerinin etrafındaki alan
        const yellows = [];
        for (let r = 0; r <= range.e.r; r++) {
          for (let c = 0; c <= range.e.c; c++) {
            const addr = XLSX.utils.encode_cell({ r, c });
            if (isInputCell(sheet[addr], addr, referencedCells)) yellows.push({ r, c });
          }
        }
        let region = null;
        if (yellows.length) {
          let minR = Infinity, minC = Infinity, maxR = 0, maxC = 0;
          yellows.forEach(p => {
            minR = Math.min(minR, p.r); minC = Math.min(minC, p.c);
            maxR = Math.max(maxR, p.r); maxC = Math.max(maxC, p.c);
          });
          region = {
            minR: Math.max(0, minR - 3), minC: Math.max(0, minC - 2),
            maxR: Math.min(range.e.r, maxR + 3), maxC: Math.min(range.e.c, maxC + 2),
          };
        }

        // 7) Inputs + outputs çıkar
        const inputs = [], outputs = [];
        const seenIn = new Set(), seenOut = new Set();
        if (region) {
          for (let r = region.minR; r <= region.maxR; r++) {
            for (let c = region.minC; c <= region.maxC; c++) {
              const addr = XLSX.utils.encode_cell({ r, c });
              const cell = sheet[addr];
              if (!cell) continue;
              if (isInputCell(cell, addr, referencedCells)) {
                if (seenIn.has(addr)) continue;
                seenIn.add(addr);
                const label = findLabel(sheet, r, c) || addr;
                inputs.push({ r, c, addr, label, value: cell.v, cell });
              } else if (cell.f) {
                if (seenOut.has(addr)) continue;
                const label = findLabel(sheet, r, c);
                if (!label) continue;
                seenOut.add(addr);
                const v = hf.getCellValue({ sheet: sheetIdHF, row: r, col: c });
                outputs.push({ r, c, addr, label, value: v, cell });
              }
            }
          }
        }

        stateRef.current = { sheet, range, hf, sheetIdHF, originalData, applied: false };
        formDataRef.current = { inputs, outputs };
        setFormData({ inputs, outputs });

        // initialValues varsa restore et
        if (initialValues && initialValues.values) {
          Object.entries(initialValues.values).forEach(([addr, v]) => {
            const cellRef = XLSX.utils.decode_cell(addr);
            try { hf.setCellContents({ sheet: sheetIdHF, row: cellRef.r, col: cellRef.c }, v); } catch (_) {}
          });
        }
        setSummary(buildSummary(sheet, range, hf, sheetIdHF));
      } catch (e) {
        console.error('ExcelCalcForm parse:', e);
        setError(e.message || 'Excel okuma hatası');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // recalc dep zinciri intentionally daralt
  }, [templateId]);

  // formData oluşunca ilk recalc — onChange snapshot'ını derhal kur (yeni kayıtta da
  // Excel'in default değerleriyle Satış Fiyatı / Kar % / Euro / Dolar dolu gelsin).
  useEffect(() => {
    if (!formData || stateRef.current.applied) return;
    stateRef.current.applied = true;
    recalc();
    // recalc dep zinciri intentionally daralt
  }, [formData]);

  const onInputChange = (inp, newVal) => {
    const { hf, sheetIdHF } = stateRef.current;
    if (!hf) return;
    let v = newVal;
    if (typeof inp.value === 'number' || (!isNaN(parseFloat(newVal)) && newVal !== '' && !inp.cell.dv?.options)) {
      const n = parseFloat(newVal);
      if (!isNaN(n)) v = n;
    }
    hf.setCellContents({ sheet: sheetIdHF, row: inp.r, col: inp.c }, v);
    recalc();
  };

  const reset = () => {
    const { hf, sheetIdHF, originalData } = stateRef.current;
    if (!hf || !originalData) return;
    if (!window.confirm('Tüm girdiler şablonun orijinal değerlerine dönecek. Onaylıyor musunuz?')) return;
    hf.setSheetContent(sheetIdHF, JSON.parse(JSON.stringify(originalData)));
    recalc();
  };

  // ─── Render ────────────────────────────────────────────
  if (!templateId) {
    return (
      <div style={emptyBox}>
        <div style={{ fontSize: 15 }}>📐 Hesap formülü için önce şablon seçin</div>
        <div style={{ fontSize: 13, marginTop: 4 }}>Üstteki "Pergole Şablonu" alanından bir şablon seçin.</div>
      </div>
    );
  }
  if (loading) return <div style={loadingBox}>Şablon yükleniyor…</div>;
  if (error) return <div style={errBox}>⚠ {error}</div>;
  if (!formData) return <div style={loadingBox}>Hazırlanıyor…</div>;
  if (formData.inputs.length === 0) {
    return <div style={errBox}>Bu Excel'de sarı dolgulu girdi hücresi bulunamadı. Şablon dosyasındaki kullanıcı girdisi olacak hücreleri sarı doldurun.</div>;
  }

  // Çıktıları sınıfla — yalnız primary (Satış Fiyatı TL), percent (Kar %) ve secondary (Euro/Dolar Satış) gösterilir;
  // sistem maliyeti, işçilik, toplam maliyet, kar tutarı vb. arka planda hesaplanır ama UI'da gizlenir.
  const primary = [], percent = [], secondary = [];
  formData.outputs.forEach(o => {
    const cls = classifyOutput(o.label);
    if (cls === 'primary') primary.push(o);
    else if (cls === 'percent') percent.push(o);
    else if (cls === 'secondary') secondary.push(o);
  });

  return (
    <div>
      {/* PRIMARY CARD — Satış Fiyatı (büyük, TL) + Kar % + Döviz (Euro/Dolar) */}
      {(primary.length > 0 || percent.length > 0 || secondary.length > 0) && (
        <div style={primaryCard}>
          {primary.map(o => (
            <div key={o.addr} style={primaryItem}>
              <div style={primaryLbl}>{o.label}</div>
              <div style={primaryVal} key={'pv-' + o.addr + '-' + tick}>{fmtCellValue(o.value, o.cell)}</div>
            </div>
          ))}
          {percent.map(o => (
            <div key={o.addr} style={primaryItemSmall}>
              <div style={primaryLbl}>{o.label}</div>
              <div style={primaryValSmall} key={'pv-' + o.addr + '-' + tick}>
                {fmtCellValue(o.value, o.cell)}{typeof o.value === 'number' ? ' %' : ''}
              </div>
            </div>
          ))}
          {secondary.map(o => (
            <div key={o.addr} style={primaryItemSmall}>
              <div style={primaryLbl}>{o.label}</div>
              <div style={primaryValSmall} key={'pv-' + o.addr + '-' + tick}>{fmtCellValue(o.value, o.cell)}</div>
            </div>
          ))}
        </div>
      )}

      {/* INPUTS */}
      <div style={section}>
        <div style={sectionHead}>📝 Veri Girişi <button type="button" onClick={reset} style={resetBtn}>↺ Sıfırla</button></div>
        <div style={fieldGrid}>
          {formData.inputs.map(inp => {
            const dvOpts = inp.cell.dv?.options;
            const liveVal = stateRef.current.hf
              ? stateRef.current.hf.getCellValue({ sheet: stateRef.current.sheetIdHF, row: inp.r, col: inp.c })
              : inp.value;
            const v = (liveVal !== null && liveVal !== undefined && typeof liveVal !== 'object') ? liveVal : (inp.value ?? '');
            if (dvOpts && dvOpts.length > 0) {
              return (
                <div key={inp.addr} style={fieldRow}>
                  <label style={fieldLabel} title={inp.addr}>{inp.label}</label>
                  <select value={String(v)} onChange={(e) => onInputChange(inp, e.target.value)} style={fieldInput}>
                    {dvOpts.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
              );
            }
            const isNum = typeof inp.value === 'number';
            return (
              <div key={inp.addr} style={fieldRow}>
                <label style={fieldLabel} title={inp.addr}>{inp.label}</label>
                <input
                  type={isNum ? 'number' : 'text'}
                  step={isNum ? 'any' : undefined}
                  value={v}
                  onChange={(e) => onInputChange(inp, e.target.value)}
                  style={fieldInput}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── styles ─────────────────────────────────────────────
const emptyBox = { padding: 32, textAlign: 'center', color: 'var(--text-tertiary, #888)', border: '1px dashed var(--border-color, #ddd)', borderRadius: 8 };
const loadingBox = { padding: 24, textAlign: 'center', color: 'var(--text-secondary, #666)' };
const errBox = { padding: 14, background: 'rgba(220,38,38,0.08)', color: '#991b1b', borderLeft: '3px solid #dc2626', borderRadius: 4 };

const primaryCard = {
  background: 'linear-gradient(135deg, #16a34a, #059669)', color: '#fff',
  padding: '20px 16px', borderRadius: 10, marginBottom: 14,
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 12,
};
const primaryItem = { textAlign: 'center', padding: '4px 8px' };
const primaryItemSmall = { ...primaryItem, opacity: 0.95 };
const primaryLbl = { fontSize: 11, opacity: 0.85, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 };
const primaryVal = { fontSize: 26, fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 };
const primaryValSmall = { ...primaryVal, fontSize: 16 };

const section = {
  background: 'var(--bg-primary, #fff)', border: '1px solid var(--border-color, #e5e5e5)',
  borderRadius: 8, padding: '12px 14px', marginBottom: 12,
};
const sectionHead = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  fontSize: 13, fontWeight: 600, color: 'var(--text-secondary, #444)',
  marginBottom: 10, paddingBottom: 6, borderBottom: '2px solid var(--bg-secondary, #f1f5f9)',
};
const resetBtn = {
  border: '1px solid var(--border-color, #ccc)', background: 'transparent',
  padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12,
  color: 'var(--text-secondary, #666)',
};
const fieldGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: '6px 14px' };
// Label content-width, input kalanı doldurur — label-input arası sabit 8px gap (sıkı görünüm)
const fieldRow = { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid var(--border-secondary)' };
const fieldLabel = {
  fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', fontWeight: 500,
  flex: '0 0 auto', whiteSpace: 'nowrap',
};
// Program standardı (.ui-input) boyut/font; sarı dolgu kullanıcı girdisi olduğunu işaretlemek için korunuyor; veri kutuları kalın font
const fieldInput = {
  flex: 1, minWidth: 0, padding: '8px 10px', boxSizing: 'border-box',
  border: '1px solid var(--border-secondary)', borderRadius: 'var(--r-md)',
  fontFamily: "'Inter', sans-serif", fontSize: 'var(--text-sm)', fontWeight: 700,
  background: '#fef9c3', color: '#7c2d12',
  outline: 'none', transition: 'border-color 0.12s, box-shadow 0.12s',
};
